// =============================================================================
// Estimate detail — faithful port of IMS Estimate_Add/Estimate_View layout.
// Shows customer block, line items grid (Inv ID/Desc/Type/Qty/Size/Price/Disc%/$/
// Total), per-type subtotals with discounts (Plants/Materials/Labor/Rental),
// whole-estimate discount, sales tax, grand total, deposit, remaining balance,
// and a cost/profit summary.
// =============================================================================
import { useEffect, useMemo, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { ArrowLeft, Printer, FileText, TrendingUp, X, Pencil, Receipt, Image as ImageIcon } from 'lucide-react'
import { toast } from 'react-toastify'
import { getEstimateWithItems, getReportPictures, getBillByEstimate, convertEstimateToBill, getSetting } from '../services/data'
import { PageHeader, Card, Spinner, Button, StatCard } from '../components/shared/ui'
import { customerLabel, currency, date } from '../lib/format'
import { printHtml } from '../lib/print'
import { estimateReportHtml, REPORT_CSS } from '../lib/reports'
import CustomerMediaPanel from '../components/CustomerMediaPanel'
import type { Estimate, EstimateItem, Customer, InventoryPicture, Bill, CustomerMedia } from '../types'

const TYPES = ['Plants', 'Materials', 'Labor', 'Rental', 'Hardgoods'] as const

// Legacy groups "Hidden Cost" into Labor for subtotal purposes.
function typeBucket(t: string | null): string {
  if (t === 'Hidden Cost') return 'Labor'
  return t ?? 'Other'
}

export default function EstimateDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [estimate, setEstimate] = useState<Estimate | null>(null)
  const [items, setItems] = useState<EstimateItem[]>([])
  const [customer, setCustomer] = useState<Customer | null>(null)
  const [loading, setLoading] = useState(true)
  const [showCostProfit, setShowCostProfit] = useState(false)
  // Legacy Estimate_Add.Print(): asks "Do you want to print this Estimate's
  // pictures?" before opening the report. askPics holds the pending print.
  const [askPics, setAskPics] = useState<{ russ: boolean } | null>(null)
  const [printing, setPrinting] = useState(false)
  const [existingBill, setExistingBill] = useState<Bill | null>(null)
  const [converting, setConverting] = useState(false)
  const [quoteText, setQuoteText] = useState<string | null>(null)
  // Customer site-visit photos the designer chose to attach to the printed estimate.
  const [selectedMedia, setSelectedMedia] = useState<Map<string, CustomerMedia>>(new Map())
  const selectedIds = useMemo(() => new Set(selectedMedia.keys()), [selectedMedia])
  const toggleMedia = (m: CustomerMedia) => setSelectedMedia(prev => {
    const next = new Map(prev)
    if (next.has(m.id)) next.delete(m.id); else next.set(m.id, m)
    return next
  })

  useEffect(() => {
    if (!id) return
    setLoading(true)
    getEstimateWithItems(id)
      .then(res => { if (res) { setEstimate(res.estimate); setItems(res.items); setCustomer(res.customer) } })
      .finally(() => setLoading(false))
    getBillByEstimate(id).then(setExistingBill).catch(() => setExistingBill(null))
    getSetting('estimate_quote_text').then(setQuoteText).catch(() => setQuoteText(null))
  }, [id])

  const makeBill = async () => {
    if (!id) return
    setConverting(true)
    try {
      const bill = await convertEstimateToBill(id)
      toast.success(existingBill ? 'Opening existing Job Invoice…' : `Job Invoice ${bill.bill_code ?? ''} created from estimate.`)
      navigate(`/bills/${bill.id}`)
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed to create Job Invoice')
    } finally {
      setConverting(false)
    }
  }

  if (loading) return <Spinner label="Loading estimate…" />
  if (!estimate) return <div className="text-gray-500">Estimate not found.</div>

  // Per-type subtotals computed from items (faithful to PlantsSubTotal/etc.).
  const typeTotals: Record<string, number> = {}
  for (const it of items) {
    const b = typeBucket(it.item_type)
    typeTotals[b] = (typeTotals[b] ?? 0) + (it.total ?? 0)
  }

  const discFor = (t: string): { p: number; d: number } => {
    switch (t) {
      case 'Plants': return { p: estimate.plant_discount_p ?? 0, d: estimate.plant_discount_d ?? 0 }
      case 'Materials': return { p: estimate.material_discount_p ?? 0, d: estimate.material_discount_d ?? 0 }
      case 'Labor': return { p: estimate.labor_discount_p ?? 0, d: estimate.labor_discount_d ?? 0 }
      case 'Rental': return { p: estimate.rental_discount_percent ?? 0, d: estimate.rental_discount ?? 0 }
      default: return { p: 0, d: 0 }
    }
  }

  const wholeEstimate = estimate.whole_estimate ?? Object.values(typeTotals).reduce((s, v) => s + v, 0)
  const wholeFinal = estimate.disc_whole_estimate ?? wholeEstimate
  const taxPct = estimate.sales_tax_percent ?? 0
  const taxAmt = estimate.sales_tax_amount ?? 0
  const grand = estimate.estimate_total ?? (wholeFinal + taxAmt)
  const deposit = estimate.deposit ?? 0
  const remaining = estimate.remaining_balance ?? (grand - deposit)

  const totalCost = items.reduce((s, i) => s + (i.cost ?? 0) * (i.qty ?? 0), 0)
  const grossProfit = grand - taxAmt - totalCost
  const margin = grand - taxAmt > 0 ? (grossProfit / (grand - taxAmt)) * 100 : 0

  const presentTypes: string[] = [
    ...TYPES.filter(t => items.some(i => typeBucket(i.item_type) === t)),
    ...Object.keys(typeTotals).filter(t => !TYPES.includes(t as typeof TYPES[number])),
  ]

  // Faithful port of Estimate_Add.Print() -> Estimate_Report. The legacy form
  // first asks whether to print the estimate's pictures; answering Yes appends
  // the EstimatePictures_Report page (plant photo + care description).
  const doPrint = async (russ: boolean, withPics: boolean) => {
    setAskPics(null)
    setPrinting(true)
    try {
      let pictures: InventoryPicture[] = []
      if (withPics) {
        try {
          pictures = await getReportPictures(
            items.map(i => ({ legacy_inventory_code: i.legacy_inventory_code, item_name: i.item_name })),
          )
        } catch { pictures = [] }
        if (pictures.length === 0) {
          toast.info("No items in this Estimate have pictures, or those that have pictures are not marked to print their pictures with the Estimate. Printing Estimate…")
        }
      }
      const mediaPhotos = Array.from(selectedMedia.values())
        .filter(m => m.kind === 'photo' && m.file_url)
        .map(m => ({ url: m.file_url as string, title: m.title }))
      const body = estimateReportHtml({ estimate, items, customer, russ, pictures, guaranteeText: quoteText, mediaPhotos })
      printHtml(`Estimate ${estimate.bill_code ?? estimate.legacy_id ?? ''}${russ ? ' (Russ)' : ''}`, body, REPORT_CSS)
    } finally {
      setPrinting(false)
    }
  }

  const PrintPicturesDialog = askPics ? (
    <div className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <div className="px-5 py-4 border-b border-gray-200">
          <h3 className="font-semibold text-lg">Print Pictures Confirmation</h3>
        </div>
        <div className="p-5 text-sm text-gray-700">
          Do you want to print this Estimate's pictures?
        </div>
        <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-gray-200">
          <Button variant="secondary" onClick={() => doPrint(askPics.russ, false)}>No</Button>
          <Button onClick={() => doPrint(askPics.russ, true)}>Yes</Button>
        </div>
      </Card>
    </div>
  ) : null

  return (
    <div>
      {PrintPicturesDialog}
      <Button variant="ghost" onClick={() => navigate('/estimates')} className="mb-4">
        <ArrowLeft size={16} /> Back to Estimates
      </Button>
      <PageHeader
        title={`Estimate ${estimate.bill_code ?? `#${estimate.legacy_id ?? ''}`}`}
        subtitle={date(estimate.estimate_date)}
        actions={
          <>
            <Button variant="secondary" onClick={() => navigate(`/estimates/${estimate.id}/edit`)}><Pencil size={16} /> Edit</Button>
            <Button variant="secondary" onClick={() => setShowCostProfit(true)}><TrendingUp size={16} /> Cost/Profit Analysis</Button>
            {existingBill ? (
              <Button variant="secondary" onClick={() => navigate(`/bills/${existingBill.id}`)}><Receipt size={16} /> View Job Invoice</Button>
            ) : (
              <Button variant="secondary" disabled={converting} onClick={makeBill}><Receipt size={16} /> {converting ? 'Creating…' : 'Make Bill'}</Button>
            )}
            <Button variant="secondary" disabled={printing} onClick={() => setAskPics({ russ: true })}><FileText size={16} /> Print Russ Version</Button>
            <Button disabled={printing} onClick={() => setAskPics({ russ: false })}><Printer size={16} /> Print</Button>
          </>
        }
      />

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <StatCard label="Grand Total" value={currency(grand)} accent="green" />
        <StatCard label="Deposit" value={currency(deposit)} accent="blue" />
        <StatCard label="Remaining" value={currency(remaining)} accent="amber" />
        <StatCard label="Gross Profit" value={currency(grossProfit)} accent={grossProfit >= 0 ? 'purple' : 'red'} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Customer + meta */}
        <Card className="p-5 lg:col-span-1">
          <h3 className="font-semibold text-gray-800 mb-3">Customer</h3>
          {customer ? (
            <div className="text-sm space-y-1 text-gray-700">
              <p className="font-medium">{customerLabel(customer)}</p>
              {customer.company_name && <p className="text-gray-500">{customer.company_name}</p>}
              <p>{[customer.address, customer.city, customer.state, customer.zip].filter(Boolean).join(', ')}</p>
              {customer.phone_home && <p>H: {customer.phone_home}</p>}
              {customer.phone_work && <p>W: {customer.phone_work}</p>}
              {customer.email && <p>{customer.email}</p>}
              <button onClick={() => navigate(`/customers/${customer.id}`)} className="text-green-700 hover:underline text-xs mt-1">View customer →</button>
            </div>
          ) : <p className="text-sm text-gray-400">No customer linked</p>}

          {estimate.customer_info && (
            <div className="mt-4 pt-4 border-t border-gray-100">
              <p className="text-xs font-medium text-gray-500 uppercase mb-1">Customer Info</p>
              <p className="text-sm text-gray-700 whitespace-pre-wrap">{estimate.customer_info}</p>
            </div>
          )}
          {estimate.comments && (
            <div className="mt-4 pt-4 border-t border-gray-100">
              <p className="text-xs font-medium text-gray-500 uppercase mb-1">Comments</p>
              <p className="text-sm text-gray-700 whitespace-pre-wrap">{estimate.comments}</p>
            </div>
          )}
          {estimate.deposit_info && (
            <div className="mt-4 pt-4 border-t border-gray-100">
              <p className="text-xs font-medium text-gray-500 uppercase mb-1">Deposit Info</p>
              <p className="text-sm text-gray-700 whitespace-pre-wrap">{estimate.deposit_info}</p>
            </div>
          )}
        </Card>

        {/* Items + totals */}
        <div className="lg:col-span-2 space-y-4">
          <Card className="overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr className="text-gray-600 border-b border-gray-200">
                    <th className="text-left font-semibold px-3 py-2">Inv ID</th>
                    <th className="text-left font-semibold px-3 py-2">Description</th>
                    <th className="text-left font-semibold px-3 py-2">Type</th>
                    <th className="text-right font-semibold px-3 py-2">Qty</th>
                    <th className="text-left font-semibold px-3 py-2">Size</th>
                    <th className="text-right font-semibold px-3 py-2">Price</th>
                    <th className="text-right font-semibold px-3 py-2">Disc%</th>
                    <th className="text-right font-semibold px-3 py-2">Disc$</th>
                    <th className="text-right font-semibold px-3 py-2">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {items.length === 0 ? (
                    <tr><td colSpan={9} className="text-center text-gray-400 py-10">No line items</td></tr>
                  ) : items.map(it => (
                    <tr key={it.id} className="border-b border-gray-100">
                      <td className="px-3 py-1.5 text-gray-500">{it.legacy_inventory_code ?? '—'}</td>
                      <td className="px-3 py-1.5">{it.item_name}</td>
                      <td className="px-3 py-1.5">{it.item_type}</td>
                      <td className="px-3 py-1.5 text-right">{it.qty}</td>
                      <td className="px-3 py-1.5">{it.item_size}</td>
                      <td className="px-3 py-1.5 text-right">{currency(it.price)}</td>
                      <td className="px-3 py-1.5 text-right">{(it.discount_percent ?? 0).toFixed(0)}%</td>
                      <td className="px-3 py-1.5 text-right">{currency(it.discount_dollar)}</td>
                      <td className="px-3 py-1.5 text-right font-medium">{currency(it.total)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>

          {/* Per-type subtotals */}
          <Card className="p-5">
            <h3 className="font-semibold text-gray-800 mb-3">Totals</h3>
            <div className="space-y-1.5 text-sm">
              {presentTypes.map(t => {
                const sub = typeTotals[t] ?? 0
                const { p, d } = discFor(t)
                const final = sub - d
                return (
                  <div key={t} className="flex justify-between text-gray-600">
                    <span>{t} {(p > 0 || d > 0) && <span className="text-xs text-gray-400">(disc {p > 0 ? `${p}%` : currency(d)})</span>}</span>
                    <span>{currency(final)}</span>
                  </div>
                )
              })}
              <div className="flex justify-between text-gray-700 pt-2 border-t border-gray-200">
                <span>Whole Estimate</span><span>{currency(wholeEstimate)}</span>
              </div>
              {(estimate.whole_estimate_discount_d ?? 0) > 0 || (estimate.whole_estimate_discount_p ?? 0) > 0 ? (
                <div className="flex justify-between text-gray-600">
                  <span>Estimate Discount {(estimate.whole_estimate_discount_p ?? 0) > 0 ? `(${estimate.whole_estimate_discount_p}%)` : ''}</span>
                  <span>-{currency((estimate.whole_estimate_discount_d ?? 0) || (wholeEstimate - wholeFinal))}</span>
                </div>
              ) : null}
              <div className="flex justify-between text-gray-700">
                <span>Estimate After Discounts</span><span>{currency(wholeFinal)}</span>
              </div>
              <div className="flex justify-between text-gray-600">
                <span>Sales Tax ({(taxPct * 100).toFixed(3).replace(/\.?0+$/, '')}%)</span><span>{currency(taxAmt)}</span>
              </div>
              <div className="flex justify-between text-lg font-bold text-gray-900 pt-2 border-t border-gray-200">
                <span>Grand Total</span><span>{currency(grand)}</span>
              </div>
              <div className="flex justify-between text-gray-600"><span>Deposit Paid</span><span>{currency(deposit)}</span></div>
              <div className="flex justify-between font-semibold text-gray-800"><span>Remaining Balance</span><span>{currency(remaining)}</span></div>
            </div>
          </Card>

          {/* Cost / profit */}
          <Card className="p-5">
            <h3 className="font-semibold text-gray-800 mb-3">Cost / Profit</h3>
            <div className="grid grid-cols-3 gap-4 text-sm">
              <div><p className="text-gray-500">Est. Cost</p><p className="font-semibold">{currency(totalCost)}</p></div>
              <div><p className="text-gray-500">Gross Profit</p><p className={`font-semibold ${grossProfit >= 0 ? 'text-green-700' : 'text-red-600'}`}>{currency(grossProfit)}</p></div>
              <div><p className="text-gray-500">Margin</p><p className="font-semibold">{margin.toFixed(1)}%</p></div>
            </div>
          </Card>
        </div>
      </div>

      {/* Site-visit media — recall the customer's photos/videos/docs and choose
          which photos to attach to the printed estimate. */}
      {customer && (
        <Card className="p-5 mt-6">
          <div className="flex items-center gap-2 mb-1">
            <ImageIcon size={16} className="text-gray-500" />
            <h3 className="font-semibold text-gray-800">Site Visit Media</h3>
            {selectedIds.size > 0 && (
              <span className="text-xs text-green-700 bg-green-50 rounded-full px-2 py-0.5">{selectedIds.size} photo{selectedIds.size > 1 ? 's' : ''} will print with this estimate</span>
            )}
          </div>
          <p className="text-sm text-gray-500 mb-4">Captured from this customer's appointments. Use the check on a photo to attach it to the printed estimate.</p>
          <CustomerMediaPanel customerId={customer.id} selectable selectedIds={selectedIds} onToggleSelect={toggleMedia} />
        </Card>
      )}

      {/* Cost/Profit Analysis modal — port of Estimate_CostProfit (per-item) */}
      {showCostProfit && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <Card className="w-full max-w-3xl max-h-[85vh] flex flex-col">
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
              <h3 className="font-semibold text-lg">Cost / Profit Analysis</h3>
              <button onClick={() => setShowCostProfit(false)} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
            </div>
            <div className="overflow-y-auto flex-1 p-4">
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr className="text-gray-600 border-b border-gray-200">
                    <th className="text-left font-semibold px-3 py-2">Item</th>
                    <th className="text-left font-semibold px-3 py-2">Type</th>
                    <th className="text-right font-semibold px-3 py-2">Qty</th>
                    <th className="text-right font-semibold px-3 py-2">Cost</th>
                    <th className="text-right font-semibold px-3 py-2">Price</th>
                    <th className="text-right font-semibold px-3 py-2">Ext. Cost</th>
                    <th className="text-right font-semibold px-3 py-2">Profit</th>
                    <th className="text-right font-semibold px-3 py-2">Margin</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map(it => {
                    const q = it.qty ?? 0
                    const extCost = (it.cost ?? 0) * q
                    const profit = (it.total ?? 0) - extCost
                    const m = (it.total ?? 0) > 0 ? (profit / (it.total ?? 1)) * 100 : 0
                    return (
                      <tr key={it.id} className="border-b border-gray-100">
                        <td className="px-3 py-1.5">{it.item_name}</td>
                        <td className="px-3 py-1.5">{it.item_type}</td>
                        <td className="px-3 py-1.5 text-right">{q}</td>
                        <td className="px-3 py-1.5 text-right">{currency(it.cost)}</td>
                        <td className="px-3 py-1.5 text-right">{currency(it.price)}</td>
                        <td className="px-3 py-1.5 text-right">{currency(extCost)}</td>
                        <td className={`px-3 py-1.5 text-right ${profit >= 0 ? 'text-green-700' : 'text-red-600'}`}>{currency(profit)}</td>
                        <td className="px-3 py-1.5 text-right">{m.toFixed(0)}%</td>
                      </tr>
                    )
                  })}
                </tbody>
                <tfoot>
                  <tr className="border-t-2 border-gray-300 font-semibold">
                    <td className="px-3 py-2" colSpan={5}>Totals</td>
                    <td className="px-3 py-2 text-right">{currency(totalCost)}</td>
                    <td className={`px-3 py-2 text-right ${grossProfit >= 0 ? 'text-green-700' : 'text-red-600'}`}>{currency(grossProfit)}</td>
                    <td className="px-3 py-2 text-right">{margin.toFixed(1)}%</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </Card>
        </div>
      )}
    </div>
  )
}
